import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { MerchantAnalyticsOverview, RecentActivity, SalesBySource } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `GET /api/v1/merchant/analytics` — DEV §9.10, named exactly. Analytics
 * (S17) audit's own scope: the merchant-side KPI set validated real
 * against this environment's transactional data before being built.
 */
export function useMerchantAnalyticsQuery(): UseQueryResult<MerchantAnalyticsOverview, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['merchant-analytics', activeShopId],
    queryFn: () =>
      apiRequest<MerchantAnalyticsOverview>('/merchant/analytics', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

/**
 * `GET /api/v1/merchant/analytics/activity` — the latest order status changes and stock movements of the active shop,
 * read from the two existing journals (nothing is stored for this view). Newest first.
 */
/** Migration 0097 — « Ventes par réseau ». */
export function useSalesBySourceQuery(): UseQueryResult<SalesBySource, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['merchant-analytics', activeShopId, 'sources'],
    queryFn: () =>
      apiRequest<SalesBySource>('/merchant/analytics/sources', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useRecentActivityQuery(limit = 20): UseQueryResult<RecentActivity, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['merchant-analytics', activeShopId, 'activity', limit],
    queryFn: () =>
      apiRequest<RecentActivity>(`/merchant/analytics/activity?limit=${String(limit)}`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}
